
import React, { useState } from 'react';
import FanDeck from '../components/home/FanDeck/FanDeck';
import VerticalCarousel from '../components/home/Inspiration/VerticalCarousel';
import HeroContent from '../components/home/HeroContent';
import HomeVisualizer from '../components/home/HomeVisualizer';
import { useTheme, useColor, useBrand } from '../context/ColorContext';
import { Zap, X } from 'lucide-react';

const Home = ({ openLightbox, onOpenVisualizer }) => {
    const { theme } = useTheme();
    const { activeColorIndex, setActiveColorIndex, isFanDeckOpen, setIsFanDeckOpen, activeColor, brandData, settings } = useColor();
    const { activeBrand } = useBrand();
    const [startY, setStartY] = useState(0);
    const [showVisualizer, setShowVisualizer] = useState(false);
    const [visualizerInitialIndex, setVisualizerInitialIndex] = useState(0);

    const handleOpenVisualizer = (index = 0) => {
        const finalIndex = typeof index === 'number' ? index : 0;
        setVisualizerInitialIndex(finalIndex);
        setShowVisualizer(true);
        if (window.innerWidth < 1024) {
            setIsFanDeckOpen(true);
        }
    };

    const handleCloseVisualizer = () => {
        setShowVisualizer(false);
        if (window.innerWidth < 1024) {
            setIsFanDeckOpen(false);
        }
    };

    const handleTouchStart = (e) => {
        setStartY(e.touches[0].clientY);
    };

    const handleTouchEnd = (e) => {
        const endY = e.changedTouches[0].clientY;
        const diff = startY - endY;

        if (diff > 50) { // Swipe Up - visualizer popup'i ac (FanDeck de otomatik aciliyor)
            if (!showVisualizer) handleOpenVisualizer();
        } else if (diff < -50) { // Swipe Down - kapat
            if (showVisualizer) {
                handleCloseVisualizer();
            } else if (isFanDeckOpen) {
                setIsFanDeckOpen(false);
            }
        }
    };

    // Anasayfa'da body scroll'unu kilitle - sayfa sabit kalsin
    React.useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    React.useEffect(() => {
        const handleEvent = () => handleOpenVisualizer();
        window.addEventListener('openHomeVisualizer', handleEvent);
        return () => window.removeEventListener('openHomeVisualizer', handleEvent);
    }, []);


    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png', // Assuming mascot exists or will be added
        'woodsol': '/img/woodsol-maskot.png' // Assuming mascot exists or will be added
    };

    const isDark = theme === 'dark';

    // Helper to get RGB for glow
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '90, 166, 104';
    };
    const primaryRgb = hexToRgb(activeBrand.primaryColor);

    // Determine if we need to show a specific kartela based on settings
    let overrideColors = null;
    let activeKartelaPrompt = null;
    const homepageKartelaId = settings?.HOMEPAGE_KARTELA_ID;

    if (homepageKartelaId && brandData?.COLLECTIONS) {
        const activeKartela = brandData.COLLECTIONS.find(k => k.id.toString() === homepageKartelaId.toString());
        if (activeKartela && activeKartela.colors && activeKartela.colors.length > 0) {
            overrideColors = activeKartela.colors;
            activeKartelaPrompt = activeKartela.aiPrompt || null;
        }
    }

    return (
        <div
            className={`relative flex flex-col lg:flex-row overflow-hidden touch-pan-x`}
            style={{ height: 'calc(100dvh / var(--app-zoom, 1))' }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >

            {/* MOBILE BLUR OVERLAY */}
            <div
                className={`fixed inset-0 z-40 lg:hidden transition-all duration-700 pointer-events-none
                    ${isFanDeckOpen ? 'opacity-100 backdrop-blur-xl bg-black/35' : 'opacity-0 backdrop-blur-none bg-transparent'}
                `}
            />

            {/* Left: Vertical Gallery (Desktop Only) */}
            <div className={`hidden lg:block w-24 fixed inset-y-0 left-0 pt-32 z-10 border-r ${theme === 'dark' ? 'border-white/5 bg-gradient-to-r from-black/20' : 'border-black/5 bg-gradient-to-r from-white/20'} to-transparent`}>
                <VerticalCarousel onImageClick={(index) => handleOpenVisualizer(index)} />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 w-full flex flex-col z-[60] pt-32 pb-32 lg:pb-0 pointer-events-none">
                {/* Center: Main Content */}
                <div className="flex-1 flex flex-col container mx-auto px-4 md:px-0 lg:pl-32 relative pointer-events-none">
                    <HeroContent openLightbox={openLightbox} onOpenVisualizer={handleOpenVisualizer} />
                </div>
            </div>

            {/* VISUALIZER OVERLAY (Mobile: Top 55vh, Desktop: Full Screen Modal) */}
            {showVisualizer && (
                <div className="fixed inset-x-0 top-0 h-[55vh] lg:inset-0 lg:h-auto z-[100] bg-black/80 lg:backdrop-blur-md flex items-start lg:items-center justify-center lg:pb-24 lg:p-12 animate-slide-down-in rounded-b-[2rem] lg:rounded-none overflow-hidden lg:overflow-visible shadow-2xl lg:shadow-none">
                    <div className="relative w-full h-full lg:max-w-[60vw] lg:h-[75vh] flex flex-col lg:items-center p-4 lg:p-0 mx-auto">
                        {/* Close Button */}
                        <div className="w-full relative flex justify-end mb-2 lg:mb-4 z-50">
                            <button
                                onClick={handleCloseVisualizer}
                                className="text-white/70 hover:text-white transition-colors flex items-center gap-2 font-black uppercase tracking-widest text-[10px] bg-black/40 lg:bg-transparent px-3 py-1.5 lg:p-0 rounded-full lg:rounded-none backdrop-blur-md lg:backdrop-blur-none"
                            >
                                <X size={20} /> Kapat
                            </button>
                        </div>
                        {/* Visualizer Container */}
                        <div className="w-full flex-1 min-h-0 lg:shadow-[0_0_100px_rgba(0,0,0,0.5)] lg:rounded-[2rem]">
                            <HomeVisualizer onOpenVisualizer={handleOpenVisualizer} isMobileSplit={true} initialIndex={visualizerInitialIndex} aiPrompt={activeKartelaPrompt} />
                        </div>
                    </div>
                </div>
            )}

            {/* Fixed Bottom: FanDeck Drawer Logic */}
            <div
                className={`fixed left-0 right-0 transition-all duration-700 cubic-bezier(0.23, 1, 0.32, 1) flex flex-col justify-end pointer-events-none
                    ${isFanDeckOpen ? 'z-[110] bottom-0 translate-y-0' : 'z-50 bottom-0 translate-y-[calc(100%-100px)] lg:translate-y-0'}
                    ${window.innerWidth >= 1024 ? `lg:h-[420px] ${showVisualizer ? 'lg:z-[110]' : 'lg:z-40'}` : (showVisualizer ? 'h-[45vh]' : 'h-[85vh]')}
                `}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div className="flex-1 pointer-events-auto h-full w-full relative">
                    <FanDeck
                        isMobile={window.innerWidth < 1024}
                        enableWheel={true}
                        overrideColors={overrideColors}
                    />
                </div>
            </div>

            {/* Right: Info Sidebar (Visual Decoration) */}
            <div className={`hidden 2xl:flex w-24 fixed inset-y-0 right-0 pt-32 flex-col justify-between items-center py-10 border-l ${theme === 'dark' ? 'border-white/5 bg-gradient-to-l from-black/20' : 'border-black/5 bg-gradient-to-l from-white/20'} to-transparent z-10`}>
                <div className={`text-xs font-bold rotate-90 tracking-widest ${theme === 'dark' ? 'text-white/30' : 'text-black/30'}`}>{"Aşağı Kaydır".toLocaleUpperCase('tr-TR')}</div>
                <div className="flex flex-col gap-4">
                    <div className={`w-1 h-16 ${theme === 'dark' ? 'bg-white/10' : 'bg-black/10'} rounded-full relative overflow-hidden`}>
                        <div className="absolute top-0 w-full h-1/2 animate-bounce" style={{ backgroundColor: activeColor?.code || activeBrand.primaryColor }}></div>
                    </div>
                </div>
                <div />
            </div>

            {/* MASCOT DECORATION - Only for DÜFA */}
            {activeBrand.id === 'dufa' && (
                <div className="hidden lg:block fixed bottom-[-12px] left-[442px] w-[235px] h-[235px] opacity-90 pointer-events-none animate-mascot-float origin-bottom z-40">
                    <img
                        src={mascotImages[activeBrand.id] || mascotImages['dufa']}
                        alt={activeBrand.name}
                        className={`w-full h-full object-contain transition-transform duration-700 transform -rotate-7 ${theme === 'dark' ? 'drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)]' : ''}`}
                        draggable="false"
                    />
                </div>
            )}
        </div>
    );
};

export default Home;
